<template>
  <v-container fluid class="edit-invoice-page pa-0">
    <v-alert v-if="exitLocked" border="left" type="warning" class="mb-3">
      <strong>This invoice passed the exit guard and is locked.</strong>
      A super admin must unlock it before any changes can be saved.
      <template v-if="canUnlockExit" v-slot:append>
        <v-btn
          small
          rounded
          color="warning"
          class="ml-3"
          :loading="unlocking"
          @click="unlockInvoice"
        >
          <v-icon left small>mdi-lock-open-variant-outline</v-icon>
          Unlock Invoice
        </v-btn>
      </template>
    </v-alert>
    <v-alert
      v-if="selectedCustomer && selectedCustomer.alert"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-3"
      v-model="selectedCustomer.alert"
      color="red"
    >
      <strong>{{ selectedCustomer.note }}</strong>
    </v-alert>

    <div class="invoice-command-bar">
      <div class="command-left">
        <div class="command-title">
          Edit Invoice
          <span v-if="Invoice.localId" class="invoice-number">
            #{{ Invoice.localId }}
          </span>
        </div>

        <div class="command-subtitle">
          <span v-if="selectedCustomer">{{ selectedCustomer.name }}</span>
          <span v-else>No customer selected</span>
        </div>
      </div>

      <div class="command-total">
        {{ invoiceTotal | currency }}
      </div>

      <div class="command-actions">
        <v-btn small rounded outlined @click.stop.prevent="cancel">
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          small
          rounded
          dark
          :loading="loading"
          :disabled="disabled"
          @click="saveInvoice"
        >
          <v-icon left small>mdi-content-save-outline</v-icon>
          Update
        </v-btn>
      </div>
    </div>

    <v-card outlined class="invoice-customer-card">
      <div class="customer-summary" @click="customerOpen = !customerOpen">
        <div>
          <div class="customer-name">
            {{ selectedCustomer ? selectedCustomer.name : "Select Customer" }}
          </div>
          <div class="customer-note">
            Tap to {{ customerOpen ? "hide" : "change" }} customer and sales rep
          </div>
        </div>

        <div class="customer-summary-right">
          <v-chip
            v-if="Invoice.due === 0"
            small
            label
            color="red lighten-3"
            text-color="red darken-4"
          >
            Paid
          </v-chip>

          <v-chip
            v-if="
              currentBusiness &&
              currentBusiness.metadata &&
              !currentBusiness.metadata.isSingleCustomerPricing &&
              PriceTiers
            "
            small
            color="red"
            text-color="white"
            class="font-weight-bold"
          >
            {{ PriceTiers.name }}
          </v-chip>

          <v-icon>
            {{ customerOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </div>
      </div>

      <v-expand-transition>
        <div v-show="customerOpen" class="invoice-customer-section">
          <SelectCustomer
            :initInvoice="initInvoice"
            :initCustomer="initCustomer"
            :initUser="initUser"
          />
        </div>
      </v-expand-transition>
    </v-card>

    <div class="product-search-sticky">
      <ProductSearchBar @submit="addRow" @submitMultiple="addMultipleRows" />
    </div>

    <div class="cart-area">
      <Cart :editMode="true" />
    </div>

    <div class="mobile-save-bar">
      <v-btn text @click.stop.prevent="cancel">
        Cancel
      </v-btn>

      <v-btn
        color="primary"
        rounded
        dark
        :loading="loading"
        :disabled="disabled"
        @click="saveInvoice"
      >
        {{ invoiceTotal | currency }} Update
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import invoiceService from "../service";
import ProductSearchBar from "@/modules/Invoice/components/ProductSearchBar";
import Cart from "@/modules/Invoice/components/Cart";
import SelectCustomer from "@/modules/Invoice/components/SelectCustomer";
import { mapActions, mapGetters } from "vuex";
import PriceTierService from "@/modules/Product/PriceTier/service.js";

export default {
  name: "edit-invoice",

  components: {
    Cart,
    SelectCustomer,
    ProductSearchBar,
  },

  data() {
    return {
      initCustomer: null,
      initInvoice: null,
      initUser: null,
      loading: false,
      unlocking: false,
      PriceTiers: null,
      customerOpen: false,
    };
  },

  computed: {
    ...mapGetters("invoice", [
      "Invoice",
      "InvoiceDetails",
      "invoiceTotal",
      "getEditInvoiceId",
      "getSelectedUser",
      "getSelectedCustomer",
    ]),

    ...mapGetters("global", ["currentBusiness", "currentUser"]),

    selectedCustomer: {
      get() {
        return this.getSelectedCustomer;
      },
      set(val) {
        this.SetSelectedCustomer(val);
      },
    },

    disabled() {
      if (this.exitLocked) return true;
      if (!this.InvoiceDetails.length) return true;

      if (
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.lockInvoiceSalesRep &&
        !this.getSelectedUser
      ) {
        return true;
      }

      return false;
    },
    exitLocked() {
      return Boolean(this.Invoice.exitDeliveredAt && !this.Invoice.exitUnlockedAt);
    },
    canUnlockExit() {
      return Boolean(this.exitLocked && this.currentUser && this.currentUser.superAdmin);
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    ...mapActions("invoice", [
      "AddInvoiceDetail",
      "ResetInvoice",
      "loadInvoice",
      "SetEditInvoiceId",
      "SetSelectedCustomer",
    ]),

    async unlockInvoice() {
      const result = await this.$swal({
        title: `Unlock invoice #${this.Invoice.localId}?`,
        text: "Explain why this cleared invoice must be reopened. It must be printed and scanned again after any changes.",
        input: "textarea",
        inputPlaceholder: "Required unlock reason",
        inputAttributes: { maxlength: 500 },
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Unlock Invoice",
        confirmButtonColor: "#f59e0b",
        inputValidator: value => {
          if (!value || value.trim().length < 5) return "Please enter an unlock reason.";
          return undefined;
        },
      });
      if (!result.value) return;

      this.unlocking = true;
      try {
        await invoiceService.unlockExit(this.Invoice.id, result.value.trim());
        await this.loadData();
        this.$swal(
          "Invoice Unlocked",
          "The invoice can now be edited. Print it after saving so the guard can clear it again.",
          "success"
        );
      } catch (error) {
        this.$swal(
          "Unable to Unlock Invoice",
          (error.data && error.data.message) || "The invoice could not be unlocked.",
          "error"
        );
      } finally {
        this.unlocking = false;
      }
    },

    cancel() {
      if (this.InvoiceDetails.length > 0) {
        this.$swal({
          title: "Are you sure?",
          text: "You want to cancel this invoice",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then((result) => {
          if (result.value) {
            this.ResetInvoice();
            this.$router.go(-1);
          }
        });

        return;
      }

      this.ResetInvoice();
      this.$router.go(-1);
    },

    async saveInvoice() {
      if (!this.Invoice.CustomerId) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Customer not selected!",
          });

        this.customerOpen = true;
        return;
      }

      this.loading = true;

      const saveInvoice = {
        ...this.Invoice,
        InvoiceDetails: this.InvoiceDetails,
      };

      return invoiceService
        .update(this.getEditInvoiceId, saveInvoice)
        .then(() => {
          this.ResetInvoice();

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "success",
              title: "Invoice was updated!",
            });

          this.$router.go(-1);
        })
        .catch((err) => {
          const message =
            err && err.data && err.data.message
              ? err.data.message
              : "Unable to update invoice";

          this.$swal("Update Invoice", message, "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getProductPrice(id) {
      return PriceTierService.getById(id).then((response) => {
        this.PriceTiers = response.data;
        return response.data;
      });
    },

    loadData() {
      const { id } = this.$route.params;

      return invoiceService.getById(id).then((response) => {
        if (response.data) {
          this.initUser = response.data.SalesRep;
          this.initCustomer = response.data.Customer;
          this.initInvoice = response.data;

          this.loadInvoice(response.data);
          this.SetEditInvoiceId(response.data.id);

          if (
            this.currentBusiness &&
            this.currentBusiness.metadata &&
            !this.currentBusiness.metadata.isSingleCustomerPricing &&
            this.initCustomer &&
            this.initCustomer.PriceTierId
          ) {
            this.getProductPrice(this.initCustomer.PriceTierId);
          } else {
            this.PriceTiers = null;
          }
        }

        return response;
      });
    },

    addRow(val) {
      if (!val) return;

      this.AddInvoiceDetail({
        ProductId: val.id,
        name: val.Bar.name,
        price: val.Bar.price,
        total: val.Bar.price * val.Bar.qty,
        qty: val.Bar.qty,
        cost: val.cost,
        boxQty: val.Bar.boxQty,
        unitCountInBox: val.unitCountInBox,
        imeiNumber: val.imeiNumber,
        Product: val,
      });
    },

    addMultipleRows(products) {
      if (!products || !products.length) return;

      products.forEach((row) => {
        this.AddInvoiceDetail({
          ProductId: row.id,
          name: row.Bar.name,
          price: row.Bar.price,
          total: row.Bar.price * row.Bar.qty,
          qty: row.Bar.qty,
          cost: row.cost,
          boxQty: row.Bar.boxQty,
          unitCountInBox: row.unitCountInBox,
          imeiNumber: row.imeiNumber,
          Product: row,
        });
      });
    },
  },
};
</script>

<style scoped>
.edit-invoice-page {
  background: #f8fafc;
  min-height: 100%;
  padding-bottom: 18px;
}

.invoice-command-bar {
  position: sticky;
  top: 64px;
  z-index: 20;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.command-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.1;
}

.invoice-number {
  color: #64748b;
  font-weight: 800;
}

.command-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 3px;
}

.command-total {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
  white-space: nowrap;
}

.command-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.invoice-customer-card {
  margin-top: 12px;
  border-radius: 18px;
  background: #ffffff;
  overflow: hidden;
}

.customer-summary {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
}

.customer-name {
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
}

.customer-note {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.customer-summary-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.invoice-customer-section {
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
}

.product-search-sticky {
  position: sticky;
  top: 138px;
  /* Keep the sticky search above cart rows, but below Vuetify menus. */
  z-index: 5;
  margin-top: 12px;
}

.cart-area {
  margin-top: 12px;
}

.mobile-save-bar {
  display: none;
}

@media (max-width: 960px) {
  .invoice-command-bar {
    top: 56px;
    grid-template-columns: 1fr auto;
  }

  .command-total {
    grid-column: 2;
    grid-row: 1;
  }

  .command-actions {
    grid-column: 1 / 3;
    justify-content: flex-end;
  }

  .product-search-sticky {
    top: 150px;
  }
}

@media (max-width: 600px) {
  .edit-invoice-page {
    padding: 10px !important;
    padding-bottom: 86px !important;
  }

  .invoice-command-bar {
    position: static;
    grid-template-columns: 1fr auto;
    padding: 12px;
  }

  .command-title {
    font-size: 18px;
  }

  .command-subtitle {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .command-total {
    font-size: 16px;
  }

  .command-actions {
    display: none;
  }

  .invoice-customer-card {
    margin-top: 10px;
  }

  .customer-summary {
    padding: 12px;
  }

  .invoice-customer-section {
    padding: 12px;
  }

  .product-search-sticky {
    position: static;
    margin-top: 10px;
  }

  .mobile-save-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 10px 12px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
  }
}
</style>
